import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import getStrategiesTool from './getStrategiesTool';
import setStrategyTool from './setStrategyTool';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import getOrCreateProductCard from '../../utils/product/getOrCreateProductCard';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import askAboutObjectsTool from '../../../../tools/general/askAboutObjectsTool/askAboutObjectsTool';
import {RelationType} from 'common/src/modules/relatedItems/relatedItems.types';
import setRelatedItemTool from './setRelatedItemsTool';
import setFeedTool from './setFeedTool';
import setSpecificItemsTool from './setSpecificItemsTool';
import searchMatchingSignalsTool from './searchMatchingSignalsTool';
import setMatchingConditionsTool from './setMatchingConditionsTool';
import searchFeedColumnsTool from './searchFeedColumnsTool';

const cardSpecificInstructions = `### What is a Product Selection card
A Product Selection card selects products from a product feed to inject into the ad context. Different strategies determine how products are selected (matching conditions, retargeting, top items, specific items). The fallback output is selected when the strategy fails to select a product.

### Product card rules
- Before setting the strategy, verify the user has provided all mandatory fields. Use the strategy's description to determine mandatory fields.
- Related item logics (product set, top items, category top items) must belong to the selected product feed.

### Property Dependencies
- The strategy must be discovered (getStrategies) before it can be set, because setStrategy requires a strategy from the search results.
- The feed depends on the strategy — setting the strategy populates the list of available feeds, so the strategy must be set before choosing a feed.
- Everything else depends on the feed:
  - Related item logics (product set, top items, category top items) are loaded when the feed is set, so the feed must be set before configuring related items.
  - Specific items require the feed to validate product IDs against.
  - Feed columns and matching signals require the feed to be set before they can be searched.
- Matching conditions depend on both feed columns and matching signals — both must be searched before conditions can be configured.`;

export default function addOrUpdateProductCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, ProductCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateProductCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateProductCardV2,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.PRODUCTS]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.PRODUCTS,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),
        askAboutObjectsTool(store),

        // Product tools
        getStrategiesTool(store),
        setStrategyTool(),
        setFeedTool(),
        setRelatedItemTool(RelationType.ProductSet),
        setRelatedItemTool(RelationType.TopItems),
        setRelatedItemTool(RelationType.CategoryTopItems),
        setSpecificItemsTool(),

        searchFeedColumnsTool(),
        searchMatchingSignalsTool(store),
        setMatchingConditionsTool(store),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateProductCardV2,
    displayName: 'Working on product card',
    description:
      'Add or update a product selection card. The card selects products from product feeds to inject into ad context for creative display or decisioning. ' +
      'Can configure product selection strategy, configure matching conditions for custom matching strategy, assign a feed, ' +
      'assign product-set/top-items/category-top-items lists if strategy supports it, or assign a specific item for the specific items strategy.',
    execute,
    inputSchema: addOrUpdateCardInputSchema,
  });
}
